import React, { useEffect, useState, useCallback } from 'react';
import { fetchMyUplinkStatus } from '../services/api';
import { MyUplinkStatus } from '../types';
import { useAuth } from '../context/AuthContext';
import { ui } from '../styles/shared';
import ErrorBanner from './shared/ErrorBanner';
import PageHeader from './shared/PageHeader';
import StatusTab from './heatpump/StatusTab';
import AlarmsTab from './heatpump/AlarmsTab';
import LiveDataTab from './heatpump/LiveDataTab';
import ApiExplorerTab from './heatpump/ApiExplorerTab';

type Tab = 'status' | 'alarms' | 'live' | 'explorer';

const TABS: { key: Tab; label: string }[] = [
    { key: 'status', label: 'Status och inställningar' },
    { key: 'alarms', label: 'Larm och meddelanden' },
    { key: 'live', label: 'Aktuella värden' },
    { key: 'explorer', label: 'API-utforskaren' },
];

// Sidan för värmepumpen: status, flikrad och meddelanden. Varje flik sköter sitt eget innehåll.
const HeatPump: React.FC = () => {
    const { timezone } = useAuth();
    const [tab, setTab] = useState<Tab>('status');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [status, setStatus] = useState<MyUplinkStatus | null>(null);
    const [statusLoading, setStatusLoading] = useState(true);

    const loadStatus = useCallback(async () => {
        setStatusLoading(true);
        try {
            setStatus(await fetchMyUplinkStatus());
        } catch (e: any) {
            setError(e.message);
        } finally {
            setStatusLoading(false);
        }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const enabled = !!status?.enabled;
    const goToStatus = useCallback(() => setTab('status'), []);

    return (
        <div>
            <PageHeader icon="🔥" title="Värmepump" description="Koppling till myUplink. Värmepumpens värden hämtas automatiskt och kan användas i larm och översikten." />

            {/* Meddelanden */}
            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
            {success && (
                <div style={ui.successBox}>
                    <span>{success}</span>
                    <button onClick={() => setSuccess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: '#155724' }}>✕</button>
                </div>
            )}

            {/* Flikrad */}
            <div className="surveillance-tabs" style={{ gap: 4 }}>
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={ui.tab(tab === t.key)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === 'status' && (
                <StatusTab status={status} loading={statusLoading} timezone={timezone}
                    reload={loadStatus} onError={setError} onSuccess={setSuccess} />
            )}
            {tab === 'alarms' && (
                <AlarmsTab enabled={enabled} timezone={timezone} onError={setError} onGoToStatus={goToStatus} />
            )}
            {tab === 'live' && (
                <LiveDataTab enabled={enabled} timezone={timezone} onError={setError} onGoToStatus={goToStatus} />
            )}
            {tab === 'explorer' && (
                <ApiExplorerTab enabled={enabled} onError={setError} onGoToStatus={goToStatus} />
            )}
        </div>
    );
};

export default HeatPump;
